import { useEffect, useRef, useState } from 'react';
import { usePathname } from '@/lib/nav/navigation';
import type { TripOption } from './TripSwitcher';
import { AppNavigationMenu } from '@/components/app/AppNavigationMenu';
import { cn } from '@/lib/utils';

interface Props {
  trips: TripOption[];
  email: string;
  /** Fallback "current trip" when the URL doesn't name one (e.g. on /new-trip). */
  lastActiveTripId?: string | null;
  showTripSwitcher?: boolean;
  showAdminNav?: boolean;
  dockedNavOpen?: boolean;
  onDockedNavOpenChange?: (open: boolean) => void;
}

/**
 * Desktop /new-trip at scroll top: no circle, border or shadow, nudged down to
 * the vertical center of the page's logo header bar.
 */
const NEW_TRIP_FLAT_MENU_BUTTON_CLASS =
  'lg:translate-y-2.5 lg:border-transparent lg:bg-transparent lg:shadow-none lg:backdrop-blur-none lg:hover:bg-black/[0.05] lg:hover:shadow-none lg:supports-[backdrop-filter]:bg-transparent';

/**
 * Compact floating launcher for the navigation drawer. It stays out of the
 * document flow so visual surfaces can use the full canvas.
 *
 * Determines the current trip from the URL: any `/trips/<uuid>` segment
 * makes that trip "current." Routes outside that pattern render no
 * switcher.
 *
 */
export function AppTopBar({
  trips,
  email,
  lastActiveTripId = null,
  showTripSwitcher = true,
  showAdminNav = false,
  dockedNavOpen = false,
  onDockedNavOpenChange,
}: Props) {
  const pathname = usePathname();
  const isNewTrip = pathname === '/new-trip' || pathname.startsWith('/new-trip/');
  const tripIdMatch = /^\/trips\/([^/]+)/i.exec(pathname);
  const currentTripId = tripIdMatch?.[1] === 'active' ? null : (tripIdMatch?.[1] ?? null);
  const [hidden, setHidden] = useState(false);
  // At the very top the menu button sits flat in the page's header bar; it
  // becomes the floating bubble only once the page has scrolled. Today only
  // /new-trip draws a header bar for it to sit in (desktop width).
  const [atTop, setAtTop] = useState(true);
  const lastScrollY = useRef(0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    setAtTop(window.scrollY < 24);

    function updateVisibility() {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;
      setAtTop(currentY < 24);

      if (currentY < 24) {
        setHidden(false);
      } else if (dockedNavOpen) {
        setHidden(false);
      } else if (delta > 8) {
        setHidden(true);
      } else if (delta < -8) {
        setHidden(false);
      }

      lastScrollY.current = currentY;
      frame.current = null;
    }

    function onScroll() {
      if (frame.current === null) {
        frame.current = window.requestAnimationFrame(updateVisibility);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame.current !== null) {
        window.cancelAnimationFrame(frame.current);
      }
    };
  }, [dockedNavOpen]);

  return (
    <div
      className={`fixed left-[calc(var(--safe-left)+0.75rem)] top-[calc(var(--safe-top)+0.625rem)] z-50 flex items-center transition-[opacity,transform] duration-200 ease-out ${
        isNewTrip ? 'max-lg:hidden' : ''
      } ${
        hidden && !dockedNavOpen
          ? 'pointer-events-none -translate-y-20 opacity-0'
          : 'translate-y-0 opacity-100'
      }`}
      aria-label={currentTripId ? 'Trip navigation' : 'Roam Together navigation'}
    >
      <AppNavigationMenu
        email={email}
        trips={trips}
        lastActiveTripId={lastActiveTripId}
        showTripSwitcher={showTripSwitcher}
        showAdminNav={showAdminNav}
        showOnDesktop
        dockedOpen={dockedNavOpen}
        onDockedOpenChange={onDockedNavOpenChange}
        triggerClassName={cn(
          dockedNavOpen && 'md:pointer-events-none md:opacity-0',
          isNewTrip && atTop && NEW_TRIP_FLAT_MENU_BUTTON_CLASS,
        )}
      />
    </div>
  );
}
